// In-galaxy shell (GDD 08): top bar with the selected system and its owner (G-7), Credits, shield and
// inbox; navigation — a bottom bar on phones, a left sidebar from 1024 px.
import type { GalaxyMeResponse, MapSystem } from '@stars-and-ships/api-types';
import {
  Bell,
  Coins,
  Map as MapIcon,
  Menu,
  Radar,
  Rocket,
  Shield,
  Star,
  Users,
} from 'lucide-react';
import { useEffect } from 'react';
import { Link, Navigate, Outlet, useLocation, useNavigate, useParams } from 'react-router';
import { ApiRequestError } from '../api/http';
import { useGalaxyMe, useMap } from '../api/queries';
import { formatDuration, formatNumber } from '../labels';
import { ownerTag } from '../ownership';
import { useLastScreen } from '../state/lastScreen';
import { useNow } from '../state/live';
import { useSelectedSystemId } from '../state/selectedSystem';
import { useGalaxySocket } from '../ws/useGalaxySocket';
import { DarkMatterIcon, IconButton, Notice } from './ui';

export interface GameContext {
  galaxyId: string;
  me: GalaxyMeResponse | undefined;
}

export function TopBar({
  galaxyId,
  me,
  system,
}: {
  galaxyId: string;
  me: GalaxyMeResponse | undefined;
  system: MapSystem | undefined;
}) {
  const navigate = useNavigate();
  const now = useNow();
  const location = useLocation();
  const inboxPath = `/g/${galaxyId}/inbox`;
  const inboxOpen = location.pathname === inboxPath;
  const shieldUntil = me?.shieldUntil ? new Date(me.shieldUntil).getTime() : 0;
  const shielded = shieldUntil > now;
  const owner = system
    ? ownerTag(system, me && { playerId: me.playerId, allianceId: me.alliance?.id ?? null })
    : undefined;
  return (
    <header className="flex h-12 shrink-0 items-center justify-between gap-3 border-b border-line bg-panel/95 px-3 lg:px-5">
      {system ? (
        <Link
          to={`/g/${galaxyId}/systems/${system.id}`}
          className="flex min-h-11 min-w-0 flex-col justify-center"
          title="Open the selected system"
        >
          <span className="truncate font-display text-sm font-bold uppercase leading-tight tracking-[0.1em] text-text">
            {system.name}
          </span>
          <span className="truncate text-[11px] leading-tight text-muted">{owner}</span>
        </Link>
      ) : (
        <span />
      )}
      <div className="flex shrink-0 items-center gap-3">
        {shielded ? (
          <span
            className="flex items-center gap-1 text-info"
            title={`Beginner shield active — ${formatDuration(shieldUntil - now)} left`}
          >
            <Shield className="size-4" aria-hidden />
            <span className="font-display text-xs font-bold uppercase tracking-[0.12em]">
              Shielded
            </span>
          </span>
        ) : null}
        <span className="flex items-center gap-1 text-credits" title="Credits">
          <Coins className="size-4" aria-hidden />
          <span className="font-mono text-sm tabular-nums text-text">
            {me ? formatNumber(me.credits) : '—'}
          </span>
          <span className="hidden font-display text-[10px] font-bold uppercase tracking-[0.14em] text-faint sm:inline">
            Credits
          </span>
        </span>
        <span className="flex items-center gap-1" title="Dark Matter">
          <DarkMatterIcon />
          <span className="font-mono text-sm tabular-nums text-text">
            {me ? formatNumber(me.darkMatter) : '—'}
          </span>
        </span>
        <IconButton
          label="Inbox"
          aria-pressed={inboxOpen}
          className={inboxOpen ? 'bg-panel-raised text-text' : ''}
          onClick={() =>
            // The bell toggles: closing returns to the screen the inbox was opened from.
            inboxOpen
              ? navigate((location.state as { from?: string } | null)?.from ?? `/g/${galaxyId}`)
              : navigate(inboxPath, { state: { from: `${location.pathname}${location.search}` } })
          }
        >
          <Bell className="size-5" aria-hidden />
          {me && me.unreadNotifications > 0 ? (
            <span className="absolute top-1.5 right-0.5 bg-accent px-1 font-mono text-[10px] font-bold text-accent-ink">
              {me.unreadNotifications}
            </span>
          ) : null}
        </IconButton>
      </div>
    </header>
  );
}

function Nav({ galaxyId }: { galaxyId: string }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const base = `/g/${galaxyId}`;
  const items = [
    { label: 'Galaxy', icon: MapIcon, to: base, active: pathname === base },
    {
      label: 'Systems',
      icon: Star,
      to: `${base}/systems`,
      active: pathname.startsWith(`${base}/systems`),
    },
    {
      label: 'Fleet',
      icon: Rocket,
      to: `${base}/fleet`,
      active: pathname.startsWith(`${base}/fleet`) || pathname.startsWith(`${base}/battle`),
    },
    { label: 'Intel', icon: Radar, to: `${base}/intel`, active: pathname === `${base}/intel` },
    {
      label: 'Alliance',
      icon: Users,
      to: `${base}/alliance`,
      active: pathname === `${base}/alliance`,
    },
    { label: 'Menu', icon: Menu, to: `${base}/menu`, active: pathname === `${base}/menu` },
  ];
  return (
    <nav
      aria-label="Main"
      className="fixed inset-x-0 bottom-0 z-30 flex border-t border-line bg-panel/95 pb-[env(safe-area-inset-bottom)] backdrop-blur lg:static lg:w-52 lg:flex-col lg:border-t-0 lg:border-r lg:pt-4 lg:pb-0"
    >
      <p className="mb-4 hidden px-5 font-display text-base font-bold tracking-[0.24em] text-accent uppercase lg:block">
        Stars &amp; Ships
      </p>
      {items.map(({ label, icon: Icon, to, active }) => (
        <button
          key={label}
          type="button"
          onClick={() => navigate(to)}
          aria-current={active ? 'page' : undefined}
          className={`flex flex-1 flex-col items-center gap-0.5 py-2 font-display text-[11px] font-bold uppercase tracking-[0.1em] lg:flex-none lg:flex-row lg:gap-3 lg:border-l-2 lg:px-5 lg:py-3 lg:text-sm ${
            active
              ? 'text-accent lg:border-accent lg:bg-accent/5'
              : 'text-faint hover:text-text lg:border-transparent'
          }`}
        >
          <Icon className="size-5" aria-hidden />
          {label}
        </button>
      ))}
    </nav>
  );
}

export function GameLayout() {
  const { galaxyId = '' } = useParams();
  const me = useGalaxyMe(galaxyId);
  const map = useMap(galaxyId);
  const selectedId = useSelectedSystemId(galaxyId, me.data?.systems[0]?.id);
  const selected = map.data?.systems.find((s) => s.id === selectedId);
  useGalaxySocket(galaxyId);
  const { pathname } = useLocation();
  const setLastScreen = useLastScreen((s) => s.set);
  useEffect(() => {
    if (!pathname.endsWith('/menu')) setLastScreen(galaxyId, pathname);
  }, [galaxyId, pathname, setLastScreen]);

  if (
    me.error instanceof ApiRequestError &&
    (me.error.code === 'NOT_IN_GALAXY' || me.error.status === 404)
  ) {
    return <Navigate to="/galaxies" replace />;
  }
  const context: GameContext = { galaxyId, me: me.data };

  return (
    <div className="hud-grid flex h-dvh flex-col bg-void lg:flex-row">
      <Nav galaxyId={galaxyId} />
      <div className="flex min-h-0 flex-1 flex-col">
        <TopBar galaxyId={galaxyId} me={me.data} system={selected} />
        <main className="relative min-h-0 flex-1 overflow-y-auto pb-16 lg:pb-0">
          {me.data && me.data.systems.length === 0 ? (
            // G-49 (decided): in the test build an admin gives the commander a new home.
            <div className="relative z-10 p-4" data-no-systems>
              <Notice tone="error">
                You have lost all your systems. Your remaining fleets hold position. Ask the test
                team for a new home system — they can respawn you with the starter package.
              </Notice>
            </div>
          ) : null}
          <Outlet context={context} />
        </main>
      </div>
    </div>
  );
}
